/**
 * @file    EngineRenderBoundary.tsx
 * @scope   VIEW
 * @summary Error boundary around engine-rendered widgets: a broken subtype shows the placeholder
 *          instead of crashing the dashboard.
 * @consumes UnsupportedWidgetPlaceholder
 * @produces EngineRenderBoundary
 * @sideEffects Logs caught render errors to the console.
 * @notes   Class component because React has no hook equivalent for error boundaries.
 */
import React from 'react';
import UnsupportedWidgetPlaceholder from './UnsupportedWidgetPlaceholder';

type EngineRenderBoundaryProps = {
  chartTypeId?: string;
  children: React.ReactNode;
};

type EngineRenderBoundaryState = { hasError: boolean };

class EngineRenderBoundary extends React.Component<
  EngineRenderBoundaryProps,
  EngineRenderBoundaryState
> {
  state: EngineRenderBoundaryState = { hasError: false };

  static getDerivedStateFromError(): EngineRenderBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error(`Engine widget "${this.props.chartTypeId}" failed to render:`, error);
  }

  render() {
    if (this.state.hasError) {
      return <UnsupportedWidgetPlaceholder chartTypeId={this.props.chartTypeId} />;
    }
    return this.props.children;
  }
}

export default EngineRenderBoundary;
